import { forEach } from 'lodash'
import sum from 'lodash/sum'
import { YearlySummary } from '../../store/redux/reducers/yearlySummarySlice'
import { roundUpToNearestMinute } from '../../store/utils/worklogUtils'

export interface SummaryData {
  workedHoursGraphData: number[]
  leftworkHoursGraphData: number[]
  overtimeHoursGraphData: number[]
  vacationHoursGraphData: number[]
  paidVacationHoursGraphData: number[]
  unpaidVacationHoursGraphData: number[]
  usedOvertimeGraphData: number[]
  sumWorked: number
  sumLeft: number
  sumOvertime: number
  sumVacation: number
  sumPaidVacation: number
  sumUnpaidVacation: number
  usedOvertime: number
  yearPercentage: number
}

type ProcessedYearlySummary = {
  workedHours: number[]
  usedOvertime: number[]
  paidVacationHours: number[]
  unpaidVacationHours: number[]
}

const processYearlySummary = (yearlySummary: YearlySummary | undefined): ProcessedYearlySummary => {
  const initialData: ProcessedYearlySummary = {
    workedHours: new Array(12).fill(0),
    usedOvertime: new Array(12).fill(0),
    paidVacationHours: new Array(12).fill(0),
    unpaidVacationHours: new Array(12).fill(0),
  }

  if (yearlySummary) {
    forEach(yearlySummary, value => {
      if (typeof value !== 'object' || value === null) return

      let i = 0
      forEach(value, (v: any, key: string) => {
        if (key === 'paidVacationHours') return

        if (!v || typeof v !== 'object') {
          i++
          return
        }

        initialData.workedHours[i] += roundUpToNearestMinute(v.work) || 0

        if (v.vacationPaid) {
          initialData.paidVacationHours[i] += v.vacationPaid
        }
        if (v.vacationUnpaid) {
          initialData.unpaidVacationHours[i] += v.vacationUnpaid
        }
        if (v.usedOvertime) {
          initialData.usedOvertime[i] += roundUpToNearestMinute(v.usedOvertime)
        }
        i++
      })
    })
  }

  return initialData
}

const calculateGraphData = (
  workedHours: number[],
  paidVacationHours: number[],
  unpaidVacationHours: number[],
  workDaysInYear: number[],
  usedOvertime: number[]
) => {
  const overtimeHoursGraphData = workedHours.map((workedHour, index) => {
    const totalHours = workDaysInYear[index]
    const availableHours = totalHours - (paidVacationHours[index] + unpaidVacationHours[index])
    return workedHour > availableHours ? workedHour - availableHours : 0
  })

  const leftworkHoursGraphData = workedHours.map((workedHour, index) => {
    const totalHours = workDaysInYear[index]
    const availableHours = totalHours - (paidVacationHours[index] + unpaidVacationHours[index])
    const usedOvertimeAtMonth = usedOvertime[index]
    const leftOvertimeData = availableHours - workedHour - usedOvertimeAtMonth
    return leftOvertimeData > 0 ? leftOvertimeData : 0
  })

  return { overtimeHoursGraphData, leftworkHoursGraphData }
}

const calculateSummaries = (
  workedHours: number[],
  leftworkHours: number[],
  overtimeHours: number[],
  paidVacationHours: number[],
  unpaidVacationHours: number[],
  usedOvertime: number[]
): Omit<
  SummaryData,
  | 'workedHoursGraphData'
  | 'leftworkHoursGraphData'
  | 'overtimeHoursGraphData'
  | 'vacationHoursGraphData'
  | 'paidVacationHoursGraphData'
  | 'unpaidVacationHoursGraphData'
  | 'usedOvertimeGraphData'
> => {
  const sumWorked = sum(workedHours)
  const sumLeft = sum(leftworkHours)
  const sumOvertime = sum(overtimeHours)
  const sumPaidVacation = sum(paidVacationHours)
  const sumUnpaidVacation = sum(unpaidVacationHours)
  const sumVacation = sum([sumPaidVacation, sumUnpaidVacation])

  const totalHours = sumWorked + sumLeft + sumOvertime + sumVacation
  const yearPercentage = totalHours > 0 ? ((sumWorked + sumOvertime) / totalHours) * 100 : 0
  const sumUsedOvertime = sum(usedOvertime)

  return {
    usedOvertime: sumUsedOvertime,
    sumWorked,
    sumLeft,
    sumOvertime,
    sumVacation,
    yearPercentage,
    sumPaidVacation,
    sumUnpaidVacation,
  }
}

export const getDataForGraph = (yearlySummary: YearlySummary | undefined, workDaysInYear: number[]): SummaryData => {
  const { workedHours, paidVacationHours, unpaidVacationHours, usedOvertime } = processYearlySummary(yearlySummary)

  const { overtimeHoursGraphData, leftworkHoursGraphData } = calculateGraphData(
    workedHours,
    paidVacationHours,
    unpaidVacationHours,
    workDaysInYear,
    usedOvertime
  )

  const summaries = calculateSummaries(
    workedHours,
    leftworkHoursGraphData,
    overtimeHoursGraphData,
    paidVacationHours,
    unpaidVacationHours,
    usedOvertime
  )

  const workedHoursGraphData = workedHours.map((workedHour, index) => workedHour - overtimeHoursGraphData[index])

  return {
    workedHoursGraphData,
    leftworkHoursGraphData,
    overtimeHoursGraphData,
    vacationHoursGraphData: [],
    paidVacationHoursGraphData: paidVacationHours,
    unpaidVacationHoursGraphData: unpaidVacationHours,
    usedOvertimeGraphData: usedOvertime,
    ...summaries,
  }
}
